/**
 * Действие сотрудника из журнала `user_action` — одной фразой. Общая для хронологии в панели
 * «Чатов» и ленты «Действия» в смене сотрудника (`/activity/<id>`).
 */
import { fmtPhone } from "@/components/chat/Contacts";
import { dt } from "@/components/timeline/Timeline";
import { day, type UserAction } from "@/lib/dealStage";

export function describeAction(a: UserAction, stageName: (code: unknown) => string): string {
  const d = a.data;
  const was = (d.was ?? {}) as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" ? v : null);
  switch (a.action) {
    case "stage": {
      let s = `стадия ${stageName(was.stage)} → ${stageName(d.now ?? d.stage)}`;
      if (str(d.wait_at)) s += `, дата ${dt(str(d.wait_at))}`;
      return s;
    }
    case "wait": {
      const old = str(was.wait_at) ? ` (было ${dt(str(was.wait_at))})` : "";
      return `дата обработки ${dt(str(d.wait_at))}${old}`;
    }
    case "rent": {
      const old = str(was.rent_start) ? ` (было ${day(str(was.rent_start))} – ${day(str(was.rent_end))})` : "";
      return `прокат ${day(str(d.start))} – ${day(str(d.end))}${old}`;
    }
    case "location":
      return `город ${str(d.city) ?? "—"}${str(was.short) ? ` (был ${str(was.short)})` : ""}`;
    case "event": {
      const s = [str(d.name) ? `«${str(d.name)}»` : null, str(d.date) ? day(str(d.date)) : null];
      return `мероприятие ${s.filter(Boolean).join(", ")}`;
    }
    case "comment":
      return `комментарий: ${str(d.text) ?? ""}`;
    case "lead_status":
      return `лид ${String(d.lead_id ?? "—")}: статус ${str(was.status_name) ?? "—"} → ${str(d.name) ?? String(d.status ?? "—")}`;
    case "phone_attach":
      return `привязал(а) номер ${fmtPhone(String(d.phone ?? ""))}`;
    case "phone_detach":
      return `отвязал(а) номер ${fmtPhone(String(d.phone ?? ""))}`;
    case "deal_create": {
      let s = `создал(а) сделку ${a.deal_id ?? "—"} на ${fmtPhone(String(d.phone ?? ""))}`;
      if (d.lead_id) s += `, лид ${d.lead_id} ${d.lead_converted ? "конвертирован" : "не закрылся"}`;
      return s;
    }
    case "chat_open":
      return "открыл(а) чат";
    case "open_bitrix":
      return "открыл(а) сделку в Bitrix";
    case "open_crm":
      return "открыл(а) сделку в CRM";
    case "zapis_link":
      return "отправил(а) ссылку на запись";
    case "sdat_link":
      return "выдал(а) ссылку на бота «сдать платье»";
    case "zapis_self": {
      const city = (d.city ?? {}) as Record<string, unknown>;
      let s = `записался сам по ссылке на ${dt(str(d.at))}`;
      if (str(city.name)) s += `, ${str(city.name)}`;
      if (d.created) s += `, создана сделка ${a.deal_id ?? "—"}`;
      return s;
    }
    case "zapis_cancel": {
      // Клиент сам отменил запись кнопкой под напоминанием о примерке (бот Telegram или MAX).
      let s = `отменил запись на ${dt(str(d.at))} кнопкой, ${str(d.via) ?? "бот"}`;
      if (d.now) s += `: стадия ${stageName(was.stage)} → ${stageName(d.now)}`;
      return s;
    }
    case "answered":
      return "отметил(а): ответили";
    case "tag_add":
      return `поставил(а) тег «${String(d.tag ?? "")}»`;
    case "tag_remove":
      return `снял(а) тег «${String(d.tag ?? "")}»`;
    case "dress_search":
      return "искал(а) платье по фото";
    case "dress_pick":
      return `отправил(а) подборку платьев${typeof d.count === "number" ? `: ${d.count}` : ""}`;
    case "dress_catalog_pick":
      return `выбрал(а) платья из каталога${Array.isArray(d.models) ? `: ${d.models.length}` : ""}`;
    case "forward_send":
      return `переслал(а) сообщение клиента в салон${str(d.group) ? ` — «${str(d.group)}»` : ""}${str(d.text) ? `: ${str(d.text)}` : ""}`;
    default:
      return a.action;
  }
}
